import { Banner } from '@/components/banner/banner.component';
import { Loading } from '@/components/loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useDepthMetricsData } from '@/modules/Report/Report.api';
import { toast } from 'react-toastify';

type FieldDensityTableProps = {
  fieldId: string;
};

type DepthMetric = {
  depth_range_cm: string;
  depth_cm: string | number;
  avg_soil_depth_mm: number;
  total_carbon_stock_tpha: number;
  cumulative_carbon_stock_tpha: number;
  mean_soc_percent: number;
  mean_total_dry_bulk_density_gpcm3: number;
  coarse_fraction_stones_pct: string;
};

export type DepthMetricsData = {
  result_set_1: DepthMetric[];
  result_set_2: DepthMetric[];
  result_set_3: {
    bd_comment: string;
    bd_threshold: string;
    field_id: string;
  }[];
};

export const FieldDensityTable = ({ fieldId }: FieldDensityTableProps) => {
  const { data: fieldDensityData, status } = useDepthMetricsData(fieldId);
  if (status === 'pending') return <Loading />;
  if (status === 'error') {
    toast.error('Failed to fetch depth metrics');
    return;
  }
  return (
    <>
      <Table className="w-full table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className={cn('border border-neutral-25 w-1/7')}>Depth Interval (cm)</TableHead>
            <TableHead className={cn('border border-neutral-25 w-1/7')}>Depth (cm)</TableHead>
            <TableHead className={cn('border border-neutral-25 w-1/7')}>Carbon Stock (t/ha)</TableHead>
            <TableHead className={cn('border border-neutral-25 w-1/7')}>Cumulative Carbon Stock (t/ha)</TableHead>
            <TableHead className={cn('border border-neutral-25 w-1/7')}>SOC%</TableHead>
            <TableHead className={cn('border border-neutral-25 w-1/7')}>Total Dry Bulk Density (g/cm³)</TableHead>
            <TableHead className={cn('border border-neutral-25 w-1/7')}>Coarse Fraction (stones) %</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className={cn('border-t-2')}>
          {fieldDensityData.result_set_1.map((row: DepthMetric, index: number) => (
            <TableRow key={index}>
              <TableHead className={cn('border border-neutral-25 w-1/7')}>{row.depth_range_cm}</TableHead>
              <TableCell className={cn('w-1/7 border border-neutral-25 text-right')}>{row.depth_cm}</TableCell>
              <TableCell className={cn('w-1/7 border border-neutral-25 text-right')}>{row.total_carbon_stock_tpha}</TableCell>
              <TableCell className={cn('w-1/7 border border-neutral-25 text-right')}>{row.cumulative_carbon_stock_tpha}</TableCell>
              <TableCell className={cn('w-1/7 border border-neutral-25 text-right')}>{row.mean_soc_percent} %</TableCell>
              <TableCell className={cn('w-1/7 border border-neutral-25 text-right')}>{row.mean_total_dry_bulk_density_gpcm3}</TableCell>
              <TableCell className={cn('w-1/7 border border-neutral-25 text-right')}>
                {`${row.coarse_fraction_stones_pct} %` || 'N/A'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {fieldDensityData.result_set_3 && fieldDensityData.result_set_3[0]?.bd_threshold && fieldDensityData.result_set_3[0]?.bd_comment && (
        <Banner
          text={
            <>
              The bulk density in this field is {fieldDensityData.result_set_3[0]?.bd_threshold}, and so is{' '}
              {fieldDensityData.result_set_3[0]?.bd_comment} for plant root growth.
            </>
          }
        />
      )}
    </>
  );
};
